import { Link } from "react-router-dom";
import type { Video } from "@/types/video";
import {
  formatCompactViews,
  formatDuration,
  formatShortDate,
  getCategoryName,
} from "@/utils/video";

interface VideoCardProps {
  video: Video;
  rank?: number;
}

export default function VideoCard({ video, rank }: VideoCardProps) {
  return (
    <Link
      to={`/detail/${video.id}`}
      className="group flex h-full flex-col overflow-hidden rounded-lg border border-background-800 bg-background-900 transition-colors duration-300 hover:border-primary-700"
    >
      <div className="relative aspect-video w-full overflow-hidden bg-background-800">
        <img
          src={video.cover}
          alt={`${video.title} 视频封面`}
          title={`${video.title} - 亚洲激情视频封面`}
          className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background-950/80 via-background-950/10 to-transparent" />

        <span className="absolute left-3 top-3 rounded bg-background-950/75 px-2 py-0.5 text-[11px] text-foreground-200 backdrop-blur-sm">
          {video.type}
        </span>

        {typeof rank === "number" && (
          <span className="absolute right-3 top-3 flex h-6 w-6 items-center justify-center rounded bg-primary-600 text-xs font-semibold text-background-50">
            {rank}
          </span>
        )}

        <span className="absolute bottom-3 right-3 rounded bg-background-950/80 px-1.5 py-0.5 text-[11px] tabular-nums text-foreground-100">
          {formatDuration(video.duration)}
        </span>

        <span className="absolute inset-0 flex items-center justify-center opacity-0 transition-opacity duration-300 group-hover:opacity-100">
          <span className="flex h-11 w-11 items-center justify-center rounded-full border border-primary-300/60 bg-primary-600/85 backdrop-blur-sm">
            <i className="ri-play-fill text-xl text-background-50" />
          </span>
        </span>
      </div>

      <div className="flex flex-1 flex-col p-4">
        <div className="flex items-center gap-2 text-[11px] text-foreground-500">
          <span className="text-primary-300">{getCategoryName(video.category)}</span>
          <span className="h-1 w-1 rounded-full bg-foreground-700" />
          <span className="tabular-nums">{formatShortDate(video.publishedAt)}</span>
        </div>

        <h3 className="mt-2 line-clamp-1 font-heading text-[15px] font-semibold text-foreground-50 transition-colors group-hover:text-primary-200">
          {video.title}
        </h3>
        <p className="mt-1.5 line-clamp-2 min-h-[36px] text-xs leading-relaxed text-foreground-500">
          {video.summary}
        </p>

        <div className="mt-auto flex items-center justify-between pt-3">
          <div className="flex flex-wrap gap-1.5">
            {video.tags.slice(0, 2).map((tag) => (
              <span
                key={tag}
                className="rounded bg-secondary-900/60 px-1.5 py-0.5 text-[10px] text-secondary-300"
              >
                #{tag}
              </span>
            ))}
          </div>
          <span className="flex items-center gap-1 whitespace-nowrap text-[11px] text-foreground-500">
            <span className="flex h-3 w-3 items-center justify-center">
              <i className="ri-eye-line text-[11px]" />
            </span>
            {formatCompactViews(video.views)}
          </span>
        </div>
      </div>
    </Link>
  );
}